/**
 * Nested App Authentication (NAA) client for the Word task pane.
 *
 * NAA lets a task-pane add-in get delegated Entra tokens for the signed-in
 * Office user WITHOUT a popup or a middle-tier, by nesting inside Office's own
 * MSAL broker. We use it only to obtain an **ID token** proving who the user
 * is; that token is handed to the key-broker, which returns the user's LiteLLM
 * virtual key (B3/B4).
 *
 * Everything here degrades safely: if auth isn't configured (no CLIENT_ID), or
 * the host is too old to support NAA, or the user isn't signed in, the callers
 * fall back to manual key entry. Nothing here ever throws to the UI — it
 * returns null and logs.
 */
import {
  createNestablePublicClientApplication,
  type IPublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { AUTHORITY, CLIENT_ID, LOGIN_SCOPES, isAuthConfigured } from './authConfig';

let clientPromise: Promise<IPublicClientApplication | null> | null = null;

/** Last interactive sign-in error, surfaced in Settings so a failing NAA flow
 *  shows the actual cause (e.g. an AADSTS redirect/consent code) instead of a
 *  generic message. Empty when the last attempt succeeded or none has run. */
let lastAuthError = '';
export function getLastAuthError(): string {
  return lastAuthError;
}

/** Explicit, clean redirect URI so it matches the Entra registration exactly.
 *  MSAL otherwise defaults to the current page URL, which Office may load with
 *  query params — never matching a clean registered URI (→ AADSTS50011). */
function redirectUri(): string {
  try {
    return `${window.location.origin}/index.html`;
  } catch {
    return 'https://word.v14.tools/index.html';
  }
}

/** True if the running Office host exposes the NAA-capable identity API. */
export function isNaaSupported(): boolean {
  try {
    return (
      typeof Office !== 'undefined' &&
      !!Office.context?.requirements?.isSetSupported?.('NestedAppAuth', '1.1')
    );
  } catch {
    return false;
  }
}

/** Lazily create the nestable MSAL client (once, memoized). Null if
 * unavailable; a failed init resolves to null rather than a rejected promise,
 * so callers uniformly fall back to manual-key mode. */
function getClient(): Promise<IPublicClientApplication | null> {
  if (!isAuthConfigured() || !isNaaSupported()) return Promise.resolve(null);
  if (!clientPromise) {
    clientPromise = createNestablePublicClientApplication({
      auth: { clientId: CLIENT_ID, authority: AUTHORITY, redirectUri: redirectUri() },
      cache: { cacheLocation: 'localStorage' },
    }).catch((err) => {
      console.warn('[NAA] client init failed — manual-key fallback:', err);
      clientPromise = null; // allow a later retry
      return null;
    });
  }
  return clientPromise;
}

/** The signed-in user's email/UPN from a token result, best-effort. */
export function emailFromResult(r: AuthenticationResult): string | null {
  const claims = (r.idTokenClaims ?? {}) as Record<string, unknown>;
  return (
    (claims.email as string) ||
    (claims.preferred_username as string) ||
    r.account?.username ||
    null
  );
}

export interface IdentityToken {
  idToken: string;
  email: string | null;
}

/**
 * Acquire an ID token silently (no UI). Returns null when auth is unavailable
 * or the user has no cached session — callers then either prompt interactively
 * (acquireInteractive) or fall back to manual key entry.
 */
export async function acquireSilent(): Promise<IdentityToken | null> {
  const pca = await getClient();
  if (!pca) return null;
  try {
    const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? undefined;
    // forceRefresh: MSAL otherwise returns the CACHED id token when the access
    // token is still valid — and that id token may already be expired, which the
    // broker rejects ("exp claim timestamp check failed"). Forcing a refresh
    // guarantees a fresh id token for the broker.
    const r = await pca.acquireTokenSilent({ scopes: LOGIN_SCOPES, account, forceRefresh: true });
    if (r.account) pca.setActiveAccount(r.account);
    return { idToken: r.idToken, email: emailFromResult(r) };
  } catch (err) {
    console.info('[NAA] silent token unavailable:', (err as Error)?.name ?? err);
    return null;
  }
}

/**
 * Acquire an ID token interactively (may show the Office-brokered account
 * picker; usually seamless because the Office user is already signed in).
 */
export async function acquireInteractive(): Promise<IdentityToken | null> {
  const pca = await getClient();
  if (!pca) return null;
  try {
    // select_account: always show the account picker, so a stale personal-MSA
    // session in the webview can't hijack the popup (the app is work-only —
    // a consumer-endpoint request fails with "unauthorized_client").
    const r = await pca.acquireTokenPopup({ scopes: LOGIN_SCOPES, prompt: 'select_account' });
    if (r.account) pca.setActiveAccount(r.account);
    lastAuthError = '';
    return { idToken: r.idToken, email: emailFromResult(r) };
  } catch (err) {
    const e = err as { errorCode?: string; errorMessage?: string; message?: string; name?: string };
    // Keep the AADSTS code / message so Settings can show the concrete cause.
    lastAuthError = [e.errorCode, e.errorMessage || e.message || e.name].filter(Boolean).join(': ').slice(0, 300);
    console.warn('[NAA] interactive sign-in failed:', err);
    return null;
  }
}

/** Silent first, then interactive. Null ⇒ caller uses manual-key fallback. */
export async function acquireIdentity(): Promise<IdentityToken | null> {
  return (await acquireSilent()) ?? (await acquireInteractive());
}

/** Read + list-ITEM write — sufficient for the sync's read and seed paths, and
 * the only scope normal (non-admin) users need. Admin-consented tenant-wide. */
export const GRAPH_RW_SCOPES = ['Sites.ReadWrite.All'];
/** List CREATION (a structural op) needs Sites.Manage.All — ReadWrite.All does
 * NOT cover it. Only the provisioning path (a site owner) requests this. */
export const GRAPH_MANAGE_SCOPES = ['Sites.Manage.All'];

/** The `scp` (delegated scopes) claim of a Graph access token, best-effort.
 * Used only to diagnose "read works, write 403s" (token lacks ReadWrite). */
export function tokenScopes(accessToken: string): string {
  try {
    const payload = accessToken.split('.')[1];
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.scp === 'string' ? json.scp : '';
  } catch {
    return '';
  }
}

/**
 * Acquire a Microsoft Graph ACCESS token (for SharePoint). Silent by default
 * (background sync must never prompt). With `interactive: true` — only from a
 * user gesture like the manual sync button — it falls back to a popup when the
 * silent token is missing OR lacks Sites.ReadWrite.All, which forces a fresh
 * token and, if needed, the consent prompt. This heals the common case where a
 * token cached before the scope was added is read-only.
 */
export async function acquireGraphToken(
  interactive = false,
  scopes: string[] = GRAPH_RW_SCOPES,
): Promise<string | null> {
  const pca = await getClient();
  if (!pca) return null;
  const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? undefined;
  const need = scopes[0].split('/').pop() ?? scopes[0];
  const covers = (t: string | null): boolean => !!t && new RegExp(need.replace('.', '\\.'), 'i').test(tokenScopes(t));

  let token: string | null = null;
  try {
    const r = await pca.acquireTokenSilent({ scopes, account });
    token = r.accessToken || null;
  } catch (err) {
    console.info('[NAA] silent Graph token unavailable:', (err as Error)?.name ?? err);
  }
  if ((!token || !covers(token)) && interactive) {
    try {
      const r = await pca.acquireTokenPopup({ scopes });
      if (r.account) pca.setActiveAccount(r.account);
      token = r.accessToken || token;
    } catch (err) {
      console.warn('[NAA] interactive Graph token failed:', err);
    }
  }
  return token;
}
